import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import mapValues from 'lodash/mapValues';
import type { DestinationAffinityCopyResult } from '~/queries/customerCommunication/requestGetDestinationAffinityCopy';
import type {
  DestinationAffinityDraftResult,
  DestinationAffinityDraftSection,
} from '~/queries/customerCommunication/requestGetDestinationAffinityDraft';

import { Button, Chip, Divider, Paper, Stack, Typography } from '@mui/material';

import { LUXURY_ESCAPES } from '~/consts/brands';
import { DESTINATION_SEGMENT_ID } from '~/consts/customerCommunication/heroPlanner';

import useCustomScheduleDestinations from '~/hooks/customerCommunication/useCustomScheduleDestinations';
import useCustomScheduleHolidayTypes from '~/hooks/customerCommunication/useCustomScheduleHolidayTypes';

import { EmptyArray } from '~/utils/arrayUtils';
import { buildCloudinaryImageUrl } from '~/utils/cloudinaryImage';

import {
  getDestinationAffinityCopyOptions,
  getDestinationAffinityHeaderImageOptions,
} from '../../utils/destinationAffinityUtils';
import { isDestinationContentOptional } from '../../utils/heroPlannerScheduleUtils';

import DestinationAffinityGenerator from './DestinationAffinityGenerator';
import GeneratedOptionsMenu, { GeneratedOptionDescription } from './GeneratedOptionsMenu';
import ScheduleHeaderImageInput, { parseHeaderImageFormData } from './Inputs/ScheduleHeaderImageInput';
import ScheduleIntroSectionForm, { parseIntroSectionFormData } from './Inputs/ScheduleIntroSectionForm';
import ScheduleTypeOptionInput, { parseTypeOptionFormData } from './Inputs/ScheduleTypeOptionInput';
import ScheduleEmailContentForm, { parseScheduleEmailContentFormData } from './ScheduleEmailContentForm';

interface DestinationFormData {
  [key: string]: string;
  webImageId: string;
  mobileImageId: string;
  headerUrl: string;
  introHeading: string;
  introCopy: string;
  introCta: string;
  introCtaUrl: string;
  emailPreHeader: string;
  emailSubjectLine: string;
}

export interface DestinationFormRef {
  getFormData: () => Promise<DestinationFormData | null>;
  validateForms: () => boolean;
}

interface Props {
  brandId: string;
  countryGroupId: string;
  countryId: string;
  offerIds: Array<string>;
  schedule?: Partial<CustomerCommunication.CustomSchedule>;
  type?: string;
  /** Whether the email already has sections, which generating with AI replaces */
  hasEmailSections?: boolean;
  /** Replaces the email's sections with generated ones. Generating with AI is only offered when given. */
  onReplaceEmailSections?: (customSections: Array<DestinationAffinityDraftSection>) => void;
  /** Swaps the email's articles for regenerated ones */
  onReplaceArticleSections?: (articleSections: Array<CustomerCommunication.CustomScheduleBrazeBannerSection>) => void;
  /** The email's sections as they're displayed now */
  getEmailSections?: () => Array<CustomerCommunication.CustomScheduleDynamicSection>;
}

type IntroSection = CustomerCommunication.CustomScheduleIntroSection;

const EMPTY_INTRO_SECTION: IntroSection = {
  headerDesktopImage: '',
  headerMobileImage: '',
  headerUrl: '',
  introHeading: '',
  introCopy: '',
  introCta: '',
  introCtaUrl: '',
};

const EMPTY_HEADER: Partial<IntroSection> = { headerDesktopImage: '', headerMobileImage: '', headerUrl: '' };

const EMPTY_INTRO: Partial<IntroSection> = { introHeading: '', introCopy: '', introCta: '', introCtaUrl: '' };

const HEADER_IMAGE_OPTION_TRANSFORM = 'c_fill,g_auto,w_192,h_108';

function toIntroSection(introSection?: Partial<IntroSection>): IntroSection {
  return mapValues(EMPTY_INTRO_SECTION, (empty, field) => introSection?.[field] || empty);
}

function hasHeaderContent(introSection: IntroSection) {
  return !!(introSection.headerDesktopImage || introSection.headerMobileImage || introSection.headerUrl);
}

function hasIntroSectionContent(introSection: IntroSection) {
  return !!(introSection.introHeading || introSection.introCopy || introSection.introCta || introSection.introCtaUrl);
}

function describeHeaderImage(image: CustomerCommunication.DestinationAffinityHeaderImage): GeneratedOptionDescription {
  return {
    primary: image.headerDesktopImage,
    secondary: image.headerMobileImage,
    imageUrl: buildCloudinaryImageUrl(image.headerDesktopImage, HEADER_IMAGE_OPTION_TRANSFORM),
  };
}

function describeSubjectLine(option: CustomerCommunication.DestinationAffinitySubjectLine): GeneratedOptionDescription {
  return { primary: option.subjectLine, secondary: option.preheader };
}

function describeIntroCopy(option: CustomerCommunication.DestinationAffinityIntroCopy): GeneratedOptionDescription {
  return {
    primary: `${option.introHeading} · ${option.introCta}`,
    secondary: option.introCopy,
  };
}

const DestinationContentForm = forwardRef<DestinationFormRef, Props>((props, ref) => {
  const {
    brandId,
    countryGroupId,
    countryId,
    offerIds,
    schedule,
    type,
    hasEmailSections = false,
    onReplaceEmailSections,
    onReplaceArticleSections,
    getEmailSections,
  } = props;

  const destinationFormRef = useRef<HTMLFormElement>(null);
  const headerImageFormRef = useRef<HTMLFormElement>(null);
  const introSectionFormRef = useRef<HTMLFormElement>(null);
  const emailContentFormRef = useRef<HTMLFormElement>(null);

  const [selectedOption, setSelectedOption] = useState(
    type === 'HOLIDAY_TYPE' ? schedule?.holidayType || '' : schedule?.destination || '',
  );
  const [introSection, setIntroSection] = useState(() => toIntroSection(schedule?.introSection));
  const [addHeader, setAddHeader] = useState(() => hasHeaderContent(introSection));
  const [addIntroSection, setAddIntroSection] = useState(() => hasIntroSectionContent(introSection));
  // the email content form keeps its own state, so new values are applied by remounting it with them as defaults
  const [emailContent, setEmailContent] = useState({
    subjectLine: schedule?.emailSubjectLine,
    preheader: schedule?.emailPreheader,
    revision: 0,
  });
  // what was generated besides what's in the email, for the team to swap in
  const [generatedOptions, setGeneratedOptions] = useState<CustomerCommunication.DestinationAffinityOptions | null>(
    null,
  );

  // Fetch type options - destinations/ holiday types
  const { dataReq: destinationsDataReq, fetchDestinations } = useCustomScheduleDestinations(brandId);
  const { dataReq: holidayTypesDataReq, fetchHolidayTypes } = useCustomScheduleHolidayTypes(brandId);

  const typeOptionConfig = useMemo(
    () =>
      type === 'HOLIDAY_TYPE'
        ? {
            name: 'holidayTypeId',
            label: 'Select holiday type',
            dataReq: holidayTypesDataReq,
            refetch: fetchHolidayTypes,
          }
        : {
            name: 'destinationId',
            label: 'Select destination',
            dataReq: destinationsDataReq,
            refetch: fetchDestinations,
          },
    [type, destinationsDataReq, holidayTypesDataReq, fetchDestinations, fetchHolidayTypes],
  );

  useEffect(() => {
    setSelectedOption(type === 'HOLIDAY_TYPE' ? schedule?.holidayType || '' : schedule?.destination || '');
  }, [schedule, type]);

  const typeOptionTitle = type === 'HOLIDAY_TYPE' ? 'Holiday Type' : 'Destination';
  const isHeaderAndIntroOptional = isDestinationContentOptional(type, brandId);
  // the generate endpoints only support Luxury Escapes destination sends
  const canGenerateWithAi =
    type === DESTINATION_SEGMENT_ID &&
    brandId === LUXURY_ESCAPES &&
    !!onReplaceEmailSections &&
    !!onReplaceArticleSections &&
    !!getEmailSections;

  useEffect(() => {
    const scheduleIntroSection = toIntroSection(schedule?.introSection);
    setIntroSection(scheduleIntroSection);
    setAddHeader(hasHeaderContent(scheduleIntroSection));
    setAddIntroSection(hasIntroSectionContent(scheduleIntroSection));
  }, [schedule]);

  // the generated options describe the destination and country they were generated for
  useEffect(() => {
    setGeneratedOptions(null);
  }, [selectedOption, countryId]);

  const updateIntroSection = useCallback((fields: Partial<IntroSection>) => {
    setIntroSection((current) => ({ ...current, ...fields }));
  }, []);

  const applyEmailContent = useCallback((subjectLine: string, preheader: string) => {
    setEmailContent((current) => ({ subjectLine, preheader, revision: current.revision + 1 }));
  }, []);

  // follows what's typed too, without remounting, so the AI options menu marks the subject line that's really there
  const handleSubjectLineChange = useCallback((subjectLine: string) => {
    setEmailContent((current) => ({ ...current, subjectLine }));
  }, []);

  // only the generated fields are assigned, so e.g. a destination without hero images keeps the team's header image
  const handleApplyDraft = useCallback(
    (draft: DestinationAffinityDraftResult) => {
      updateIntroSection(draft.draft.introSection);
      // the draft always links the header and the intro CTA to the destination's search
      setAddHeader(true);
      setAddIntroSection(true);
      setGeneratedOptions({
        headerImages: getDestinationAffinityHeaderImageOptions(draft),
        subjectLines: [],
        introCopy: [],
      });
      onReplaceEmailSections(draft.draft.customSections);
    },
    [updateIntroSection, onReplaceEmailSections],
  );

  // only the copy changes: the offers, header and anything else the team has edited stay as they are
  const handleApplyCopy = useCallback(
    (copy: DestinationAffinityCopyResult) => {
      applyEmailContent(copy.draft.emailSubjectLine, copy.draft.emailPreheader);
      updateIntroSection(copy.draft.introSection);
      setAddIntroSection(true);
      setGeneratedOptions((current) => ({
        headerImages: current?.headerImages ?? [],
        ...getDestinationAffinityCopyOptions(copy),
      }));
      onReplaceArticleSections(copy.draft.customSections);
    },
    [applyEmailContent, updateIntroSection, onReplaceArticleSections],
  );

  const handleToggleHeader = () => {
    if (addHeader) {
      updateIntroSection(EMPTY_HEADER);
    }
    setAddHeader(!addHeader);
  };

  const handleToggleIntroSection = () => {
    if (addIntroSection) {
      updateIntroSection(EMPTY_INTRO);
    }
    setAddIntroSection(!addIntroSection);
  };

  const validateForms = useCallback(() => {
    const shouldValidateHeader = !isHeaderAndIntroOptional || addHeader;
    const shouldValidateIntroSection = !isHeaderAndIntroOptional || addIntroSection;

    return (
      destinationFormRef.current?.reportValidity() &&
      (!shouldValidateHeader || headerImageFormRef.current?.reportValidity()) &&
      (!shouldValidateIntroSection || introSectionFormRef.current?.reportValidity()) &&
      emailContentFormRef.current?.reportValidity()
    );
  }, [addHeader, addIntroSection, isHeaderAndIntroOptional]);

  const getFormData = useCallback(async () => {
    if (!validateForms()) {
      return null;
    }

    const shouldIncludeHeader = !isHeaderAndIntroOptional || addHeader;
    const shouldIncludeIntroSection = !isHeaderAndIntroOptional || addIntroSection;

    const typeOptionData = parseTypeOptionFormData(new FormData(destinationFormRef.current), typeOptionConfig.name);
    const headerImageData = shouldIncludeHeader
      ? parseHeaderImageFormData(new FormData(headerImageFormRef.current))
      : { webImageId: '', mobileImageId: '', headerUrl: '' };
    const introSectionData = shouldIncludeIntroSection
      ? parseIntroSectionFormData(new FormData(introSectionFormRef.current))
      : { introHeading: '', introCopy: '', introCta: '', introCtaUrl: '' };
    const emailContentData = parseScheduleEmailContentFormData(new FormData(emailContentFormRef.current));

    const formDataValues = {
      ...typeOptionData,
      ...headerImageData,
      ...introSectionData,
      ...emailContentData,
    };
    return formDataValues;
  }, [addHeader, addIntroSection, isHeaderAndIntroOptional, validateForms, typeOptionConfig]);

  useImperativeHandle(ref, () => ({
    getFormData,
    validateForms,
  }));

  return (
    <Stack direction="column" gap={3}>
      <Paper variant="outlined" sx={{ p: 2, position: 'relative' }}>
        <Stack direction="column" gap={2}>
          <Typography variant="h4">{typeOptionTitle}</Typography>
          <ScheduleTypeOptionInput
            ref={destinationFormRef}
            name={typeOptionConfig.name}
            label={typeOptionConfig.label}
            value={selectedOption}
            onChange={setSelectedOption}
            dataReq={typeOptionConfig.dataReq}
          />
          {canGenerateWithAi && (
            <>
              <Divider />
              <DestinationAffinityGenerator
                brand={brandId}
                country={countryId}
                destination={selectedOption}
                hasExistingContent={
                  hasEmailSections || hasHeaderContent(introSection) || hasIntroSectionContent(introSection)
                }
                onApplyDraft={handleApplyDraft}
                onApplyCopy={handleApplyCopy}
                getEmailSections={getEmailSections}
              />
            </>
          )}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, position: 'relative' }}>
        <Stack direction="column" gap={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
            <Typography variant="h4">Email Content</Typography>
            <GeneratedOptionsMenu
              title="AI-written subject lines and pre-headers"
              options={generatedOptions?.subjectLines ?? EmptyArray}
              describe={describeSubjectLine}
              isSelected={(option) => option.subjectLine === emailContent.subjectLine}
              onSelect={(option) => applyEmailContent(option.subjectLine, option.preheader)}
            />
          </Stack>
          <ScheduleEmailContentForm
            key={emailContent.revision}
            ref={emailContentFormRef}
            countryGroupId={countryGroupId}
            countryId={countryId}
            offerIds={offerIds}
            showPrefillButton={false}
            preHeaderDefaultValue={emailContent.preheader}
            subjectLineDefaultValue={emailContent.subjectLine}
            onSubjectLineChange={handleSubjectLineChange}
          />
        </Stack>
      </Paper>

      {isHeaderAndIntroOptional && (
        <Stack direction="row" spacing={2}>
          <Button
            variant={addHeader ? 'outlined' : 'contained'}
            color={addHeader ? 'error' : 'primary'}
            size="small"
            onClick={handleToggleHeader}
          >
            {addHeader ? 'Remove Header' : 'Add Header'}
          </Button>

          <Button
            variant={addIntroSection ? 'outlined' : 'contained'}
            color={addIntroSection ? 'error' : 'primary'}
            size="small"
            onClick={handleToggleIntroSection}
          >
            {addIntroSection ? 'Remove Intro Section' : 'Add Intro Section'}
          </Button>
        </Stack>
      )}

      {(!isHeaderAndIntroOptional || addHeader) && (
        <Paper variant="outlined" sx={{ p: 2, position: 'relative' }}>
          <Stack direction="column" gap={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
              <Typography variant="h4">Header Image</Typography>
              <GeneratedOptionsMenu
                title="Destination hero images"
                options={generatedOptions?.headerImages ?? EmptyArray}
                describe={describeHeaderImage}
                isSelected={(option) => option.headerDesktopImage === introSection.headerDesktopImage}
                onSelect={updateIntroSection}
              />
            </Stack>
            <form ref={headerImageFormRef}>
              <ScheduleHeaderImageInput
                webImageId={introSection.headerDesktopImage}
                mobileImageId={introSection.headerMobileImage}
                headerUrl={introSection.headerUrl}
                onWebImageIdChange={(headerDesktopImage) => updateIntroSection({ headerDesktopImage })}
                onMobileImageIdChange={(headerMobileImage) => updateIntroSection({ headerMobileImage })}
                onHeaderUrlChange={(headerUrl) => updateIntroSection({ headerUrl })}
                isOptional={isHeaderAndIntroOptional}
              />
            </form>
          </Stack>
        </Paper>
      )}

      {(!isHeaderAndIntroOptional || addIntroSection) && (
        <Paper variant="outlined" sx={{ p: 2, position: 'relative' }}>
          <Stack direction="column" gap={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
              <Typography variant="h4">Intro Section</Typography>
              <GeneratedOptionsMenu
                title="AI-written intros"
                options={generatedOptions?.introCopy ?? EmptyArray}
                describe={describeIntroCopy}
                isSelected={(option) => option.introHeading === introSection.introHeading}
                onSelect={updateIntroSection}
              />
            </Stack>
            <ScheduleIntroSectionForm
              ref={introSectionFormRef}
              introHeading={introSection.introHeading}
              introCopy={introSection.introCopy}
              introCta={introSection.introCta}
              introCtaUrl={introSection.introCtaUrl}
              onIntroHeadingChange={(introHeading) => updateIntroSection({ introHeading })}
              onIntroCopyChange={(introCopy) => updateIntroSection({ introCopy })}
              onIntroCtaChange={(introCta) => updateIntroSection({ introCta })}
              onIntroCtaUrlChange={(introCtaUrl) => updateIntroSection({ introCtaUrl })}
              isOptional={isHeaderAndIntroOptional}
            />
          </Stack>
        </Paper>
      )}

      <Divider>
        <Chip label="Additional Sections" />
      </Divider>
    </Stack>
  );
});

DestinationContentForm.displayName = 'DestinationContentForm';
export default DestinationContentForm;
